import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  FolderOpen, 
  ExternalLink, 
  FileText, 
  Image, 
  File,
  Download,
  Info
} from "lucide-react";
import api from "../lib/api";

export default function MaterialeDigitalePage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get("/settings/digital-materials");
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  const hasExternalLink = settings?.drive_url && settings.drive_url.trim() !== "";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Materiale Digitale</h1>
        <p className="text-muted-foreground">
          Documentazione, cataloghi e risorse aziendali
        </p>
      </div>

      {/* External Drive Link */}
      {hasExternalLink && (
        <Card className="border-accent/50 bg-accent/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-accent" />
              Archivio Documenti
            </CardTitle>
            <CardDescription>
              {settings?.drive_description || "Accedi all'archivio completo dei documenti aziendali"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="bg-accent hover:bg-accent/90"
              onClick={() => window.open(settings.drive_url, "_blank")}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Apri Archivio Documenti
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Document Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => hasExternalLink && window.open(settings.drive_url, "_blank")}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Cataloghi Prodotti</h3>
                <p className="text-sm text-muted-foreground">Listini e schede tecniche</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => hasExternalLink && window.open(settings.drive_url, "_blank")}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-100 text-green-600">
                <Image className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Materiale Marketing</h3>
                <p className="text-sm text-muted-foreground">Immagini e brochure</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => hasExternalLink && window.open(settings.drive_url, "_blank")}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
                <Download className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Modulistica</h3>
                <p className="text-sm text-muted-foreground">Documenti e moduli</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => hasExternalLink && window.open(settings.drive_url, "_blank")}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-100 text-amber-600">
                <File className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Certificazioni</h3>
                <p className="text-sm text-muted-foreground">Certificati e attestati</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box if no link configured */}
      {!hasExternalLink && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Archivio non configurato</p>
                <p className="text-sm text-amber-700 mt-1">
                  L'amministratore deve configurare il link all'archivio documenti nelle Impostazioni.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
